import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Typography,
  Stack,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  IconButton,
} from '../../../ui/core';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useToast } from '../../../hooks';
import { updateWaypointOrder, getDailyTrips } from '../../../redux/Trips/actions';
import DragIndicatorIcon from '../../../ui/icons/DragIndicatorIcon';

const WaypointCard = ({ waypoint, rides, index, isDragging }) => {
  const location = waypoint[0].type === 'pick-up' ? rides[0]?.pickUp : rides[0]?.dropOff;
  const waypointType = waypoint[0].type;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 2,
        mb: 1,
        borderRadius: '8px',
        backgroundColor: isDragging ? 'action.selected' : 'background.paper',
        border: '1px solid',
        borderColor: isDragging ? 'primary.main' : 'divider',
        boxShadow: isDragging ? 3 : 0,
        transition: 'all 0.2s ease',
      }}
    >
      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 2, cursor: 'grab' }} />
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: waypointType === 'pick-up' ? 'success.main' : 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
          flexShrink: 0,
        }}
      >
        <Typography color="white" variant="body2" fontWeight={700}>
          {index + 1}
        </Typography>
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {location?.name || location?.address || 'Unknown location'}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {location?.address}
        </Typography>
        <Box display="flex" alignItems="center" mt={0.5}>
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              backgroundColor: waypointType === 'pick-up' ? 'success.light' : 'error.light',
              color: waypointType === 'pick-up' ? 'success.dark' : 'error.dark',
              fontWeight: 600,
              mr: 1,
            }}
          >
            {waypointType}
          </Typography>
          <Stack direction="row" spacing={-1}>
            {rides.map((ride) => {
              const rideId = ride?.id || ride?.rideId;
              return (
                <Avatar
                  key={rideId}
                  sx={{ width: 24, height: 24, border: '2px solid white' }}
                  src={ride?.rider?.imageUrl}
                />
              );
            })}
          </Stack>
          <Typography variant="caption" color="text.secondary" ml={1}>
            {rides.map((r) => r?.rider?.firstName).filter(Boolean).join(' & ')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ReorderWaypointsModal = ({ trip, open, onClose }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const [waypoints, setWaypoints] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && trip?.waypoints) {
      const validWaypoints = trip.waypoints
        .map((w, originalIndex) => ({ waypoint: w, originalIndex }))
        .filter((item) => item.waypoint && item.waypoint.length > 0);
      setWaypoints(validWaypoints);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRidesForWaypoint = (waypoint) => {
    return waypoint.map((waypointItem) =>
      trip.rides.find((ride) => {
        const rideId = ride.id || ride.rideId;
        return rideId === waypointItem.ride_id;
      }),
    ).filter(Boolean);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(waypoints);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setWaypoints(items);
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const serializeWaypoints = (wps) =>
    wps.map((group) => group.map((item) => ({ type: item.type, ride_id: item.ride_id })));

  const handleSave = async () => {
    setIsSaving(true);
    const newOrder = serializeWaypoints(waypoints.map((item) => item.waypoint));
    const { success } = await updateWaypointOrder(trip.id, newOrder)(dispatch);

    if (success) {
      t.success('Waypoint order updated successfully');
      const date = trip.departureTime?.split('T')[0];
      if (date) {
        await getDailyTrips(date)(dispatch);
      }
      setIsSaving(false);
      onClose();
    } else {
      setIsSaving(false);
      t.error('Failed to update waypoint order');
    }
  };

  const hasChanges = () => {
    if (!trip?.waypoints || waypoints.length === 0) return false;
    const originalOrder = serializeWaypoints(trip.waypoints.filter((w) => w && w.length > 0));
    const newOrder = serializeWaypoints(waypoints.map((item) => item.waypoint));
    return JSON.stringify(originalOrder) !== JSON.stringify(newOrder);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Reorder Waypoints
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Drag and drop to reorder the trip stops
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 2, minHeight: 300 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="waypoints">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef}>
                {waypoints.map((item, index) => (
                  <Draggable
                    key={`waypoint-${item.originalIndex}`}
                    draggableId={`waypoint-${item.originalIndex}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{ cursor: 'grab' }}
                      >
                        <WaypointCard
                          waypoint={item.waypoint}
                          rides={getRidesForWaypoint(item.waypoint)}
                          index={index}
                          isDragging={snapshot.isDragging}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button color="error" disabled={isSaving} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaving || !hasChanges()}
          loading={isSaving}
          onClick={handleSave}
        >
          Save Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReorderWaypointsModal;
